import type {
  Direction,
  GameSnapshot,
  InputCommand as CoreInputCommand,
  PlayerId,
} from "../../game-core/src/types";

export const PROTOCOL_VERSION = 1 as const;
export const PROTOCOL_NAME = "neon-fuse-match" as const;
export const MAX_INPUT_LEAD_TICKS = 6;
export const MAX_INPUT_LAG_TICKS = 12;
export const RECONNECT_WINDOW_MS = 30_000;
export const MAX_MESSAGE_BYTES = 16_384;

export type ProtocolVersion = typeof PROTOCOL_VERSION;
export type InputCommand = CoreInputCommand;

export interface MatchIdentity {
  readonly roomId: string;
  readonly mapSeed: number;
  readonly rulesetVersion: string;
  readonly contentVersion: string;
}

export type ProtocolErrorCode =
  | "invalid-message"
  | "message-too-large"
  | "unsupported-version"
  | "already-joined"
  | "not-joined"
  | "invalid-player"
  | "player-unavailable"
  | "room-full"
  | "input-too-old"
  | "input-too-far"
  | "sequence-replayed"
  | "reconnect-expired"
  | "server-error";

export type DisconnectReason =
  | "server-shutdown"
  | "match-ended"
  | "protocol-error"
  | "replaced-session"
  | "auth-required";

export interface JoinMessage {
  readonly type: "join";
  readonly protocolVersion: number;
  readonly requestedPlayerId?: PlayerId;
  readonly displayName?: string;
}

export interface InputMessage {
  readonly type: "input";
  readonly protocolVersion: number;
  readonly playerId: PlayerId;
  readonly clientTick: number;
  readonly sequence: number;
  readonly input: InputCommand;
}

export interface ReconnectMessage {
  readonly type: "reconnect";
  readonly protocolVersion: number;
  readonly playerId: PlayerId;
  readonly reconnectToken: string;
  readonly lastServerTick: number;
  readonly lastAckSequence: number;
}

export type ClientMessage = JoinMessage | InputMessage | ReconnectMessage;

export interface HelloMessage {
  readonly type: "hello";
  readonly protocolVersion: ProtocolVersion;
  readonly protocolName: typeof PROTOCOL_NAME;
  readonly identity: MatchIdentity;
  readonly tickRate: number;
  readonly maxPlayers: number;
}

export interface JoinedMessage {
  readonly type: "joined";
  readonly protocolVersion: ProtocolVersion;
  readonly identity: MatchIdentity;
  readonly playerId: PlayerId;
  readonly reconnectToken: string;
  readonly serverTick: number;
  readonly snapshot: GameSnapshot;
}

export interface SnapshotMessage {
  readonly type: "snapshot";
  readonly protocolVersion: ProtocolVersion;
  readonly identity: MatchIdentity;
  readonly serverTick: number;
  readonly ackSequence: number;
  readonly snapshot: GameSnapshot;
}

export interface ReconnectAcceptedMessage {
  readonly type: "reconnect-accepted";
  readonly protocolVersion: ProtocolVersion;
  readonly identity: MatchIdentity;
  readonly playerId: PlayerId;
  readonly serverTick: number;
  readonly reconnectExpiresAt: number;
  readonly snapshot: GameSnapshot;
}

export interface ErrorMessage {
  readonly type: "error";
  readonly protocolVersion: ProtocolVersion;
  readonly code: ProtocolErrorCode;
  readonly message: string;
}

export interface DisconnectMessage {
  readonly type: "disconnect";
  readonly protocolVersion: ProtocolVersion;
  readonly reason: DisconnectReason;
  readonly reconnectable: boolean;
  readonly message: string;
}

export type ServerMessage =
  | HelloMessage
  | JoinedMessage
  | SnapshotMessage
  | ReconnectAcceptedMessage
  | ErrorMessage
  | DisconnectMessage;

export type ProtocolMessage = ClientMessage | ServerMessage;

export class ProtocolError extends Error {
  public readonly code: ProtocolErrorCode;

  public constructor(code: ProtocolErrorCode, message: string) {
    super(message);
    this.name = "ProtocolError";
    this.code = code;
  }
}

function utf8ByteLength(value: string): number {
  let length = 0;
  for (const character of value) {
    const codePoint = character.codePointAt(0) ?? 0;
    if (codePoint <= 0x7f) {
      length += 1;
    } else if (codePoint <= 0x7ff) {
      length += 2;
    } else if (codePoint <= 0xffff) {
      length += 3;
    } else {
      length += 4;
    }
  }
  return length;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function requiredString(
  value: unknown,
  field: string,
  maxLength: number,
): string {
  if (
    typeof value !== "string" ||
    value.length === 0 ||
    value.length > maxLength
  ) {
    throw new ProtocolError(
      "invalid-message",
      `${field} must be a non-empty string of at most ${maxLength} characters`,
    );
  }
  return value;
}

function optionalString(
  record: Record<string, unknown>,
  field: string,
  maxLength: number,
): string | undefined {
  const value = record[field];
  if (value === undefined) {
    return undefined;
  }
  return requiredString(value, field, maxLength);
}

function requiredInteger(
  value: unknown,
  field: string,
  minimum: number,
): number {
  if (!Number.isSafeInteger(value) || (value as number) < minimum) {
    throw new ProtocolError(
      "invalid-message",
      `${field} must be an integer of at least ${minimum}`,
    );
  }
  return value as number;
}

function optionalDirection(
  value: unknown,
  field: string,
): Direction | undefined {
  if (value === undefined) {
    return undefined;
  }
  if (
    value !== "up" &&
    value !== "down" &&
    value !== "left" &&
    value !== "right"
  ) {
    throw new ProtocolError(
      "invalid-message",
      `${field} must be up, down, left, or right`,
    );
  }
  return value;
}

function validateInput(value: unknown): InputCommand {
  if (!isRecord(value)) {
    throw new ProtocolError("invalid-message", "input must be an object");
  }

  const booleanFields = [
    "up",
    "down",
    "left",
    "right",
    "bomb",
    "action",
    "detonate",
    "kick",
    "punch",
    "line",
  ] as const;
  for (const field of booleanFields) {
    const fieldValue = value[field];
    if (fieldValue !== undefined && typeof fieldValue !== "boolean") {
      throw new ProtocolError(
        "invalid-message",
        `input.${field} must be boolean`,
      );
    }
  }

  const direction = optionalDirection(value.direction, "input.direction");
  const move = optionalDirection(value.move, "input.move");

  return {
    ...(value.up === undefined ? {} : { up: value.up as boolean }),
    ...(value.down === undefined ? {} : { down: value.down as boolean }),
    ...(value.left === undefined ? {} : { left: value.left as boolean }),
    ...(value.right === undefined ? {} : { right: value.right as boolean }),
    ...(direction === undefined ? {} : { direction }),
    ...(move === undefined ? {} : { move }),
    ...(value.bomb === undefined ? {} : { bomb: value.bomb as boolean }),
    ...(value.action === undefined ? {} : { action: value.action as boolean }),
    ...(value.detonate === undefined
      ? {}
      : { detonate: value.detonate as boolean }),
    ...(value.kick === undefined ? {} : { kick: value.kick as boolean }),
    ...(value.punch === undefined ? {} : { punch: value.punch as boolean }),
    ...(value.line === undefined ? {} : { line: value.line as boolean }),
  };
}

function validateVersion(value: unknown): number {
  const version = requiredInteger(value, "protocolVersion", 1);
  if (version !== PROTOCOL_VERSION) {
    throw new ProtocolError(
      "unsupported-version",
      `protocol version ${version} is not supported`,
    );
  }
  return version;
}

function parseJoin(record: Record<string, unknown>): JoinMessage {
  const protocolVersion = validateVersion(record.protocolVersion);
  const requestedPlayerId = optionalString(record, "requestedPlayerId", 32);
  const displayName = optionalString(record, "displayName", 48);
  return {
    type: "join",
    protocolVersion,
    ...(requestedPlayerId === undefined ? {} : { requestedPlayerId }),
    ...(displayName === undefined ? {} : { displayName }),
  };
}

function parseInput(record: Record<string, unknown>): InputMessage {
  const protocolVersion = validateVersion(record.protocolVersion);
  return {
    type: "input",
    protocolVersion,
    playerId: requiredString(record.playerId, "playerId", 32),
    clientTick: requiredInteger(record.clientTick, "clientTick", 0),
    sequence: requiredInteger(record.sequence, "sequence", 0),
    input: validateInput(record.input),
  };
}

function parseReconnect(record: Record<string, unknown>): ReconnectMessage {
  const protocolVersion = validateVersion(record.protocolVersion);
  return {
    type: "reconnect",
    protocolVersion,
    playerId: requiredString(record.playerId, "playerId", 32),
    reconnectToken: requiredString(
      record.reconnectToken,
      "reconnectToken",
      128,
    ),
    lastServerTick: requiredInteger(record.lastServerTick, "lastServerTick", 0),
    lastAckSequence: requiredInteger(
      record.lastAckSequence,
      "lastAckSequence",
      -1,
    ),
  };
}

export function decodeClientMessage(serialized: string): ClientMessage {
  if (utf8ByteLength(serialized) > MAX_MESSAGE_BYTES) {
    throw new ProtocolError(
      "message-too-large",
      `messages must be at most ${MAX_MESSAGE_BYTES} bytes`,
    );
  }

  let value: unknown;
  try {
    value = JSON.parse(serialized) as unknown;
  } catch {
    throw new ProtocolError("invalid-message", "message must be valid JSON");
  }
  if (!isRecord(value)) {
    throw new ProtocolError("invalid-message", "message must be a JSON object");
  }

  switch (value.type) {
    case "join":
      return parseJoin(value);
    case "input":
      return parseInput(value);
    case "reconnect":
      return parseReconnect(value);
    default:
      throw new ProtocolError(
        "invalid-message",
        "message type must be join, input, or reconnect",
      );
  }
}

export function encodeMessage(message: ProtocolMessage): string {
  return JSON.stringify(message);
}

export function isClientMessage(serialized: string): boolean {
  try {
    decodeClientMessage(serialized);
    return true;
  } catch {
    return false;
  }
}
